class Mapping {
    id: number;
    name: string;

    private _tables: Array<TableNameInputModel> = null;
    get tables() {
        if (this._tables == null) {
            this.loadTablesMetrics();
        }
        return this._tables;
    }
    get visibleTables() {
        return this.tables.filter(x => x.visibility === VisibilityEnum.Normal);
    }

    private _metrics: Array<FieldNameInputModel> = null;
    get metrics() {
        if (this._metrics == null) {
            this.loadTablesMetrics();
        }
        return this._metrics;
    }
    get visibleMetrics() {
        return this.metrics.filter(x => x.visibility === VisibilityEnum.Normal);
    }

    private _filters: Array<FilterTemplate>;
    get filters() {
        if (!this._filters) {
            this.loadFilters();
        }

        return this._filters;
    }
    get visibleFilters() {
        return this.filters.filter(x => x.visibility === VisibilityEnum.Normal);
    }

    //***Reports shown on Admin
    private _originalReports: Array<ReportTemplate>;
    get originalReports() {
        if (!this._originalReports) {
            this.loadReports(true);
        }

        return this._originalReports;
    }
    get visibleOriginalReports() {
        return this.originalReports.filter(x => x.visibility);
    }
    //***

    private _reports: Array<ReportTemplate>;
    get reports() {
        if (!this._reports) {
            this.loadReports(false);
        }

        return this._reports;
    }
    get visibleReports() {
        return this.reports.filter(x => x.visibility);
    }

    private _drilldowns: Array<ReportStub>;
    get drilldowns() {
        if (!this._drilldowns) {
            this.loadDrilldowns();
        }

        return this._drilldowns;
    }
    get visibleDrilldowns() {
        return this.drilldowns.filter(x => x.visibility === VisibilityEnum.Normal);
    }

    constructor(data) {
        this.id = data?.id ?? 0;
        this.name = data?.name ?? "";
    }

    getMetricFieldModel(metricId: number) {
        if (!metricId)
            return null;
        var metric = this.metrics.find(m => m.metricId === metricId);
        return metric;
    }

    getMetric(metricId: number) {
        let metricFieldModel = this.getMetricFieldModel(metricId);
        if (!metricFieldModel)
            return null;
        var metric = new Metric({
            id: metricFieldModel.metricId,
            metricName: metricFieldModel.displayName,
            originalName: metricFieldModel.originalName,
            table: null,
            formulaTop: null,
            formulaBottom: null,
            calculateTotals: null,
            groupingType: metricFieldModel.groupingType,
            decimalPlaces: metricFieldModel.decimalPlaces,
            format: metricFieldModel.format,
            accountingStyle: metricFieldModel.accountingStyle,
            formattingRule: metricFieldModel.formattingRuleId,
            backgroundColor: metricFieldModel.backgroundColor,
            foreColor: metricFieldModel.foreColor,
            thresholdColor: metricFieldModel.thresholdColor,
            thresholdValue: metricFieldModel.thresholdValue,
            tableName: metricFieldModel.tableName,
            visibility: metricFieldModel.visibility,
        });
        return metric;
    }

    getMetricName(metricId: number) {
        var metric = this.getMetricFieldModel(metricId);
        return metric ? metric.nodeName : "";
    }

    getField(fieldId: number) {
        if (!fieldId)
            return null;

        let table = this.tables.find(t => t.fields.find(f => f.fieldId === fieldId));
        let field = table ? table.fields.find(f => f.fieldId === fieldId) : null;
        return field;
    }

    getFieldName(fieldId: number) {
        var field = this.getField(fieldId);
        return field ? field.nodeName : "";
    }

    getFilter(id: number) {
        return this.filters.find(x => x.id === id);
    }

    getFilterName(filterId: number) {
        var filter = this.getFilter(filterId);
        return filter ? filter.name : "";
    }

    removeOriginalReportById(reportId: number) {
        let report = this.originalReports.find(x => x.id === reportId);
        if (report) {
            this.removeOriginalReport(report);
        }
    }
    removeOriginalReport(report: ReportTemplate) {
        const index = this.originalReports.findIndex(x => x === report);
        if (index !== -1) {
            this.forceReloadReports();
            this.originalReports.splice(index, 1);
        }
    }
    addOriginalReport(report: ReportTemplate) {
        this.originalReports.push(report);
        this.forceReloadReports();
    }

    getOriginalReport(reportId: number) {
        return this.originalReports.find(x => x.id === reportId);
    }

    forceReloadReports() {
        this._reports = null;
    }
    forceReloadDrilldowns() {
        this._drilldowns = null;
    }

    private loadFilters() {
        let self = this;
        let o = {
            url: `${urlConfigs.designer}GetDataSourceFiltersFromMapping?mappingId=${this.id}`,
            verb: "GET",
            data: { mappingId: this.id },
            dataType: "json",
            async: false,
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            self._filters = createInstances(FilterTemplate, data.schema.filters);
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to get the mapping filters`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }

    private loadReports(isAdmin: boolean) {
        let self = this;
        let o = {
            url: `${urlConfigs.designer}GetDataSourceReportsFromMapping`,
            data: { mappingId: this.id, isAdmin: isAdmin },
            dataType: "json",
            async: false,
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            if (isAdmin) {
                self._originalReports = createInstancesWithParameter(ReportTemplate, data.schema.reports, true);
            }
            else {
                self._reports = createInstancesWithParameter(ReportTemplate, data.schema.reports, false);
            }
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to get the mapping reports`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }

    private loadTablesMetrics() {
        let self = this;
        let o = {
            url: urlConfigs.designer + "GetDataSource",
            data: { mappingId: this.id },
            dataType: "json",
            async: false,
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            self._tables = createInstances(TableNameInputModel, data.schema.tables);
            self._metrics = createInstances(FieldNameInputModel, data.schema.metrics);
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to get mapping`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }

    private loadDrilldowns() {
        let self = this;
        let o = {
            url: `${urlConfigs.drilldownReport}GetDrilldownReports`,
            data: { mappingId: this.id },
            dataType: "json",
            async: false,
        } as RequestServerParams;
        o.successFunction = function (drilldowns, e) {
            self._drilldowns = createInstances(ReportStub, drilldowns);
            let order = 0;
            for (let drilldown of self._drilldowns) {
                drilldown.order = ++order;
            }
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to get the mapping drilldowns`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }
}